import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import PinInput from "../components/PinInput";
import "./css/InformationAccount.css";
import {
  API_ROUTES,
  fetchAndStoreServerPublicKey,
  buildExtraInfo,
  setNewPassword,
} from "../utils/apiClient";
import rsaController from "../utils/RSA-controller";
import AppConstants from "../utils/appConstants";
import { getPasswordRequirements } from "../utils/passwordRequirements";

const parseResponseCode = (decryptedData) => {
  let data = decryptedData;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (e) {
      const codeMatch = data.match(/"CODE"\s*:\s*(\d+)/);
      if (codeMatch) {
        data = { CODE: parseInt(codeMatch[1], 10) };
      }
    }
  }

  let responseCode = data?.CODE ?? data?.code;
  if (responseCode == null && data?.body) {
    responseCode = data.body.CODE ?? data.body.code;
  }
  if (responseCode != null) {
    responseCode = typeof responseCode === "string" ? parseInt(responseCode, 10) : responseCode;
  }
  return { data, responseCode };
};

const decryptApiResponse = async (responseData) => {
  let result = responseData;
  if (responseData?.MESSAGES) {
    try {
      if (Array.isArray(responseData.MESSAGES)) {
        let fullDecryptedMessage = "";
        for (const encryptedSegment of responseData.MESSAGES) {
          const decryptedSegment = await rsaController.decryptWithUserKey(encryptedSegment);
          fullDecryptedMessage += decryptedSegment;
        }
        result = JSON.parse(fullDecryptedMessage);
      } else if (typeof responseData.MESSAGES === "string") {
        const decryptedMessage = await rsaController.decryptWithUserKey(responseData.MESSAGES);
        result = JSON.parse(decryptedMessage);
      }
    } catch (decryptError) {
      result = responseData;
    }
  }
  return result?.body || result;
};

const encryptPublicRequest = async (body) => {
  const publicKey = await rsaController.getUserPublicKey();
  const extraInfo = await buildExtraInfo();

  const messagesObject = {
    HEADERS: extraInfo,
    BODY: body,
  };

  const encryptedMessages = await rsaController.encryptJsonForServer(messagesObject);

  return {
    PUBLIC_KEY: publicKey,
    MESSAGES: encryptedMessages,
    TYPE: "0",
    VERSION_CODE: "0.5.9",
  };
};

export default function ForgetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState(false);

  useEffect(() => {
    const initializeKeys = async () => {
      try {
        await rsaController.generateUserKeyPair();
        await fetchAndStoreServerPublicKey();
      } catch (initError) {
        setError("Failed to initialize. Please refresh the page.");
      }
    };

    initializeKeys();
  }, []);

  const sendVerificationCode = async () => {
    if (!email) {
      setEmailError(true);
      setError("");
      return;
    }

    setSendingCode(true);
    setError("");
    setEmailError(false);

    try {
      const requestBody = await encryptPublicRequest({ USERNAME: email });

      const response = await axios.post(API_ROUTES.SEND_RESET_PIN_OTP, requestBody, {
        headers: { "Content-Type": "application/json; charset=UTF-8" },
      });

      const finalResult = await decryptApiResponse(response.data);

      if (
        finalResult?.success ||
        finalResult?.CODE === AppConstants.responseOk ||
        finalResult?.CODE === AppConstants.messagesOK ||
        finalResult?.status === "success" ||
        response.status === AppConstants.responseOk ||
        response.status === 200
      ) {
        setShowPin(true);
        setError("");
      } else {
        setError(
          finalResult?.message ||
            finalResult?.MESSAGE ||
            "Failed to send verification code. Please try again."
        );
      }
    } catch (requestError) {
      const errorData = requestError.response?.data;
      const finalErrorResult = await decryptApiResponse(errorData);

      setError(
        finalErrorResult?.message ||
          finalErrorResult?.MESSAGE ||
          errorData?.message ||
          errorData?.MESSAGE ||
          "Failed to send verification code. Please try again."
      );
    } finally {
      setSendingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await sendVerificationCode();
  };

  const handleVerify = async () => {
    if (!email) {
      setEmailError(true);
      setError("");
      return;
    }

    if (!verificationCode || verificationCode.trim() === "") {
      setError("Please enter the verification code");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const requestBody = await encryptPublicRequest({
        USERNAME: email,
        VERIFICATION_CODE: verificationCode,
      });

      const response = await axios.post(API_ROUTES.VERIFY_RESET_PIN_OTP, requestBody, {
        headers: { "Content-Type": "application/json; charset=UTF-8" },
      });

      const finalResult = await decryptApiResponse(response.data);

      if (response.status === 200) {
        if (
          finalResult?.success ||
          finalResult?.CODE === AppConstants.responseOk ||
          finalResult?.CODE === AppConstants.messagesOK ||
          finalResult?.status === "success"
        ) {
          setError("");
          setShowPin(false);
          setVerificationCode("");
          setShowPasswordReset(true);
        } else {
          setError(
            finalResult?.message ||
              finalResult?.MESSAGE ||
              "Invalid verification code. Please try again."
          );
        }
      } else {
        setError(
          finalResult?.message ||
            finalResult?.MESSAGE ||
            "Invalid verification code. Please try again."
        );
      }
    } catch (requestError) {
      const errorData = requestError.response?.data;
      const finalErrorResult = await decryptApiResponse(errorData);

      setError(
        finalErrorResult?.message ||
          finalErrorResult?.MESSAGE ||
          errorData?.message ||
          errorData?.MESSAGE ||
          "Invalid verification code. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();

    if (password !== passwordConfirmation) {
      setError(t("personalInformation.password.errors.passwordMismatch"));
      return;
    }

    const requirements = getPasswordRequirements(password);
    if (!Object.values(requirements).every(Boolean)) {
      setError(t("personalInformation.password.errors.passwordPolicy"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await setNewPassword(email, password, passwordConfirmation);
      const { data, responseCode } = parseResponseCode(response.decryptedData);

      if (
        responseCode === AppConstants.responseOk ||
        responseCode === AppConstants.messagesOK ||
        data?.success ||
        data?.status === "success"
      ) {
        navigate("/home", { state: { passwordResetSuccess: true } });
      } else {
        setError(
          data?.MESSAGE ||
            data?.message ||
            t("forgetPassword.errors.resetFailed") ||
            "Failed to reset password. Please try again."
        );
      }
    } catch (requestError) {
      const errorData = requestError.response?.data;
      const finalErrorResult = await decryptApiResponse(errorData);

      setError(
        finalErrorResult?.MESSAGE ||
          finalErrorResult?.message ||
          requestError.message ||
          t("forgetPassword.errors.resetFailed") ||
          "Failed to reset password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditEmail = () => {
    setShowPin(false);
    setShowPasswordReset(false);
    setVerificationCode("");
    setPassword("");
    setPasswordConfirmation("");
    setError("");
    setEmailError(false);
  };

  const passwordRequirements = getPasswordRequirements(password);
  const allPasswordRequirementsMet = Object.values(passwordRequirements).every(Boolean);

  return (
    <div className="page-container">
      <div className="login-card">
        <h1 className="login-title">
          {showPasswordReset
            ? t("forgetPassword.newPasswordTitle") || "Set New Password"
            : t("login.forgotPassword") || "Forgot Password"}
        </h1>

        {!showPin && !showPasswordReset && (
          <form onSubmit={handleSubmit}>
            <label className="input-label">{t("login.username") || "Email"}</label>
            <input
              type="email"
              className="login-input"
              placeholder={t("login.usernamePlaceholder") || "Enter your email"}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (e.target.value) setEmailError(false);
                if (error) setError("");
              }}
              disabled={sendingCode}
              style={emailError ? { borderColor: "red" } : {}}
            />
            {sendingCode && (
              <p
                style={{
                  color: "#ffd100",
                  fontSize: "12px",
                  marginBottom: "10px",
                  textAlign: "center",
                }}
              >
                Sending verification code...
              </p>
            )}
            {error && (
              <div
                style={{
                  color: "red",
                  fontSize: "14px",
                  marginBottom: "10px",
                  padding: "10px",
                  backgroundColor: "rgba(255, 230, 230, 0.9)",
                  borderRadius: "4px",
                  fontWeight: "bold",
                }}
              >
                {error}
              </div>
            )}
            <button type="submit" className="login-btn" disabled={sendingCode}>
              {sendingCode
                ? t("common.loading") || "Loading..."
                : t("deviceVerification.resendButton") || "Send Code"}
            </button>
          </form>
        )}

        {showPasswordReset && (
          <form onSubmit={handleSetPassword}>
            <p className="email-step-subtitle" style={{ marginBottom: "16px" }}>
              {t("forgetPassword.newPasswordSubtitle") ||
                "Choose a new password for your account."}
            </p>

            <label className="input-label">{t("login.username") || "Email"}</label>
            <input
              type="email"
              className="login-input"
              value={email}
              readOnly
              disabled
              style={{ opacity: 0.8, marginBottom: "12px" }}
            />

            <label className="input-label">
              {t("personalInformation.password.fields.password") || "Password"}
            </label>
            <div className="input-wrapper input-wrapper--password" style={{ marginBottom: "12px" }}>
              <input
                type={showPassword ? "text" : "password"}
                className="login-input"
                placeholder={t("personalInformation.password.placeholders.password")}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError("");
                }}
                autoComplete="new-password"
                style={{ marginBottom: 0 }}
              />
              <button
                type="button"
                className="input-password-toggle"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
              >
                <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
              </button>
            </div>

            <label className="input-label">
              {t("personalInformation.password.fields.passwordConfirmation") ||
                "Confirm Password"}
            </label>
            <div className="input-wrapper input-wrapper--password" style={{ marginBottom: "12px" }}>
              <input
                type={showPasswordConfirmation ? "text" : "password"}
                className="login-input"
                placeholder={t("personalInformation.password.placeholders.passwordConfirmation")}
                value={passwordConfirmation}
                onChange={(e) => {
                  setPasswordConfirmation(e.target.value);
                  if (error) setError("");
                }}
                autoComplete="new-password"
                style={{ marginBottom: 0 }}
              />
              <button
                type="button"
                className="input-password-toggle"
                onClick={() => setShowPasswordConfirmation((prev) => !prev)}
                aria-label={
                  showPasswordConfirmation ? t("login.hidePassword") : t("login.showPassword")
                }
              >
                <i
                  className={`fa-solid ${showPasswordConfirmation ? "fa-eye-slash" : "fa-eye"}`}
                />
              </button>
            </div>

            <ul className="password-requirements" aria-live="polite" style={{ marginBottom: "16px" }}>
              {[
                { key: "symbol", met: passwordRequirements.symbol },
                { key: "uppercase", met: passwordRequirements.uppercase },
                { key: "lowercase", met: passwordRequirements.lowercase },
                { key: "number", met: passwordRequirements.number },
                { key: "minLength", met: passwordRequirements.minLength },
              ].map(({ key, met }) => (
                <li key={key} className={`password-requirement ${met ? "met" : ""}`}>
                  <span className="password-requirement-box" aria-hidden="true">
                    {met ? <i className="fa-solid fa-check" /> : null}
                  </span>
                  <span>{t(`personalInformation.password.requirements.${key}`)}</span>
                </li>
              ))}
            </ul>

            {error && (
              <div
                style={{
                  color: "red",
                  fontSize: "14px",
                  marginBottom: "10px",
                  padding: "10px",
                  backgroundColor: "rgba(255, 230, 230, 0.9)",
                  borderRadius: "4px",
                  fontWeight: "bold",
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              className="login-btn"
              disabled={loading || !allPasswordRequirementsMet}
            >
              {loading
                ? t("common.loading") || "Loading..."
                : t("forgetPassword.resetButton") || "Reset Password"}
            </button>

            <button
              type="button"
              onClick={handleEditEmail}
              className="verification-edit-link"
              style={{ display: "block", marginTop: "12px", width: "100%", textAlign: "center" }}
            >
              {t("personalInformation.verification.editEmail") || "Edit Email"}
            </button>
          </form>
        )}
      </div>

      {showPin && (
        <div className="verification-overlay">
          <div className="verification-card">
            <h1 className="verification-title">
              {t("personalInformation.verification.title") || "Verification"}
            </h1>
            <p className="verification-subtitle">
              {t("personalInformation.verification.subtitle") ||
                "Enter the verification code sent to your email"}
            </p>

            <PinInput length={5} onChange={setVerificationCode} />

            {sendingCode && (
              <p
                style={{
                  color: "#ffd100",
                  fontSize: "12px",
                  marginBottom: "10px",
                  textAlign: "center",
                }}
              >
                Sending verification code...
              </p>
            )}

            {error && <p className="verification-error">{error}</p>}

            <button
              className="verification-button"
              onClick={handleVerify}
              disabled={
                loading || !verificationCode || verificationCode.trim() === "" || sendingCode
              }
            >
              {loading
                ? t("common.loading") || "Loading..."
                : t("personalInformation.verification.verifyButton") || "Verify"}
            </button>

            <button type="button" onClick={handleEditEmail} className="verification-edit-link">
              {t("personalInformation.verification.editEmail") || "Edit Email"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
